import React, { useState, useEffect, useRef } from 'react';
import { Send, Flag, MoreVertical, Shield, Volume2, VolumeX, Search, Smile, Image, Paperclip, X, AlertTriangle, CheckCircle, Clock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import axios from 'axios';

/**
 * SYSTÈME DE CHAT DE GROUPE COMPLET POUR LIKILIMBA
 * =================================================
 * 
 * Fonctionnalités implémentées :
 * - Conversation temps réel avec WebSocket
 * - Interface moderne et accessible
 * - Modération admin complète
 * - Notifications et messages non lus
 * - Sécurité et vie privée
 * - Accessibilité avec synthèse vocale
 * - Design responsive et intuitif
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api` || '/api';

// Hook pour WebSocket temps réel
const useWebSocket = (groupId, user, onMessageReceived) => {
  const [socket, setSocket] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');

  useEffect(() => {
    if (!groupId || !user) return;

    // Simule WebSocket pour mode preview/development
    if (process.env.NODE_ENV === 'development') {
      setConnectionStatus('connected (simulation)');
      
      // Simule réception de messages périodiques
      const interval = setInterval(() => {
        if (Math.random() > 0.95) { // 5% chance chaque seconde
          const mockMessage = {
            id: `mock-${Date.now()}`,
            group_id: groupId,
            sender_id: 'mock-user',
            sender_name: 'Utilisateur Test',
            message: 'Message de démonstration temps réel',
            timestamp: new Date().toISOString(),
            is_flagged: false
          };
          onMessageReceived?.(mockMessage);
        }
      }, 1000);

      return () => clearInterval(interval);
    }

    // WebSocket réel en production
    try {
      const wsUrl = `wss://${window.location.host}/ws/chat/${groupId}?token=${localStorage.getItem('token')}`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setConnectionStatus('connected');
        console.log('✅ Chat WebSocket connecté');
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          onMessageReceived?.(message);
        } catch (error) {
          console.error('Erreur parsing message WebSocket:', error);
        }
      };

      ws.onclose = () => {
        setConnectionStatus('disconnected');
        console.log('🔌 Chat WebSocket déconnecté');
      };

      ws.onerror = (error) => {
        setConnectionStatus('error');
        console.error('❌ Erreur WebSocket:', error);
      };

      setSocket(ws);

      return () => {
        ws.close();
      };
    } catch (error) {
      console.error('❌ Erreur création WebSocket:', error);
      setConnectionStatus('error');
    }
  }, [groupId, user]);

  return { socket, connectionStatus };
};

// Composant Message individuel
const ChatMessage = ({ 
  message, 
  currentUser, 
  canModerate, 
  onFlag, 
  onDelete, 
  onSpeak,
  isNew = false 
}) => {
  const [showActions, setShowActions] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const isOwnMessage = message.sender_id === currentUser.id;

  // Animation pour nouveaux messages
  const messageClass = `
    transition-all duration-300 
    ${isNew ? 'animate-in slide-in-from-right-2 fade-in' : ''}
    ${isDeleted ? 'opacity-50 scale-95' : ''}
  `;

  const handleFlag = () => {
    onFlag(message.id, message);
    setShowActions(false);
  };

  const handleDelete = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
      setIsDeleted(true);
      await onDelete(message.id);
    }
  };

  const handleSpeak = () => {
    onSpeak(message.message, message.sender_name);
  };

  // Format de l'heure
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffMinutes < 1) return "À l'instant";
    if (diffMinutes < 60) return `Il y a ${diffMinutes}min`;
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('fr-FR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`group mb-4 ${messageClass}`}>
      <div className={`flex items-start gap-3 ${isOwnMessage ? 'flex-row-reverse' : ''}`}>
        {/* Avatar */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
            {message.sender_name?.charAt(0)?.toUpperCase() || '?'}
          </div>
        </div>

        {/* Message content */}
        <div className={`flex-1 max-w-xs sm:max-w-md ${isOwnMessage ? 'text-right' : ''}`}>
          {/* Sender info */}
          {!isOwnMessage && (
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-semibold text-gray-900">
                {message.sender_name}
              </span>
              {message.is_admin && (
                <Shield className="w-3 h-3 text-blue-600" title="Administrateur" />
              )}
            </div>
          )}

          {/* Message bubble */}
          <div className={`relative rounded-2xl px-4 py-2 shadow-sm ${
            isOwnMessage 
              ? 'bg-blue-600 text-white' 
              : 'bg-white border border-gray-200'
          } ${message.is_flagged ? 'ring-2 ring-orange-400' : ''}`}>
            
            {/* Flagged indicator */}
            {message.is_flagged && (
              <div className="absolute -top-2 -right-2 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-3 h-3 text-white" />
              </div>
            )}

            {/* Message text */}
            <p className={`text-sm leading-relaxed ${
              isOwnMessage ? 'text-white' : 'text-gray-900'
            }`}>
              {message.message}
            </p>

            {/* Timestamp avec indicateurs de statut */}
            <div className={`text-xs mt-1 flex items-center justify-between ${
              isOwnMessage ? 'text-blue-100' : 'text-gray-500'
            }`}>
              <span>{formatTime(message.timestamp)}</span>
              
              {/* Indicateurs de statut pour messages propres */}
              {isOwnMessage && (
                <div className="flex items-center ml-2">
                  {message.sending && (
                    <div className="flex items-center">
                      <div className="w-3 h-3 animate-spin rounded-full border border-blue-200 border-t-transparent mr-1"></div>
                      <span className="text-xs">Envoi...</span>
                    </div>
                  )}
                  
                  {message.sent && (
                    <div className="flex items-center text-green-400">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      <span className="text-xs">Envoyé</span>
                    </div>
                  )}
                  
                  {message.failed && (
                    <div className="flex items-center text-red-400">
                      <X className="w-3 h-3 mr-1" />
                      <span className="text-xs">Échec</span>
                    </div>
                  )}
                  
                  {message.is_read && !message.sending && !message.sent && !message.failed && (
                    <CheckCircle className="w-3 h-3 text-blue-200" title="Lu" />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions menu */}
        <div className={`flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ${
          showActions ? 'opacity-100' : ''
        }`}>
          <div className="relative">
            <button
              onClick={() => setShowActions(!showActions)}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Actions du message"
            >
              <MoreVertical className="w-4 h-4 text-gray-400" />
            </button>

            {/* Actions dropdown */}
            {showActions && (
              <div className="absolute right-0 top-8 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 min-w-[140px]">
                
                {/* Lecture vocale */}
                <button
                  onClick={handleSpeak}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                >
                  <Volume2 className="w-4 h-4" />
                  Lire à voix haute
                </button>

                {/* Signaler (si pas son propre message) */}
                {!isOwnMessage && (
                  <button
                    onClick={handleFlag}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-orange-600"
                  >
                    <Flag className="w-4 h-4" />
                    Signaler
                  </button>
                )}

                {/* Supprimer (si admin ou son propre message) */}
                {(canModerate || isOwnMessage) && (
                  <button
                    onClick={handleDelete}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600"
                  >
                    <X className="w-4 h-4" />
                    Supprimer
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant Zone de saisie
const ChatInput = ({ onSendMessage, disabled = false, placeholder = "Tapez votre message..." }) => {
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const textareaRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedMessage = message.trim();
    
    if (!trimmedMessage || disabled) return;
    
    onSendMessage(trimmedMessage);
    setMessage('');
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Auto-resize textarea
  const handleInputChange = (e) => {
    setMessage(e.target.value);
    
    // Auto-resize
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white border-t border-gray-200 p-4">
      <div className="flex items-end gap-3">
        
        {/* Message input */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className="w-full resize-none border border-gray-300 rounded-2xl px-4 py-3 pr-12 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-sm max-h-[120px] overflow-y-auto"
            rows={1}
            aria-label="Message à envoyer"
          />
          
          {/* Emoji button */}
          <button
            type="button"
            className="absolute right-3 bottom-3 p-1 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Ajouter un émoji"
          >
            <Smile className="w-5 h-5" />
          </button>
        </div>

        {/* Send button */}
        <button
          type="submit"
          disabled={!message.trim() || disabled}
          className="bg-blue-600 text-white p-3 rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
          aria-label="Envoyer le message"
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
      
      {/* Character counter */}
      {message.length > 200 && (
        <div className="text-xs text-gray-500 mt-2 text-right">
          {message.length}/500 caractères
        </div>
      )}
    </form>
  );
};

// Panneau de modération admin
const ModerationPanel = ({ 
  group, 
  flaggedMessages = [], 
  onDeleteMessage, 
  onUnflagMessage, 
  isVisible = false,
  onClose 
}) => {
  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
        
        {/* Header */}
        <div className="bg-red-50 border-b border-red-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-red-600" />
              <h2 className="text-lg font-bold text-red-900">
                Modération - {group.name}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-red-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-red-600" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {flaggedMessages.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Aucun message signalé
              </h3>
              <p className="text-gray-600">
                Toutes les conversations se déroulent dans le respect.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">
                Messages signalés ({flaggedMessages.length})
              </h3>
              
              {flaggedMessages.map((message) => (
                <div key={message.id} className="border border-red-200 rounded-lg p-4 bg-red-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-sm">{message.sender_name}</span>
                        <span className="text-xs text-gray-500">
                          {new Date(message.timestamp).toLocaleString()}
                        </span>
                        <div className="bg-red-100 px-2 py-1 rounded-full text-xs text-red-800">
                          Signalé {message.flag_count} fois
                        </div>
                      </div>
                      
                      <p className="text-gray-900 text-sm bg-white p-3 rounded border">
                        {message.message}
                      </p>
                      
                      {message.flag_reasons && (
                        <div className="mt-2 text-xs text-red-700">
                          Motifs : {message.flag_reasons.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 mt-3 pt-3 border-t border-red-200">
                    <button
                      onClick={() => onDeleteMessage(message.id)}
                      className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors"
                    >
                      Supprimer
                    </button>
                    <button
                      onClick={() => onUnflagMessage(message.id)}
                      className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700 transition-colors"
                    >
                      Ignorer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Composant principal GroupChat
const GroupChat = ({ 
  group, 
  user, 
  messages: initialMessages = [], 
  onSendMessage,
  onBack,
  onClose,
  unreadCount = 0,
  onMarkAsRead
}) => {
  // États
  const [messages, setMessages] = useState(initialMessages);
  const [newMessages, setNewMessages] = useState([]);
  const [showModerationPanel, setShowModerationPanel] = useState(false);
  const [flaggedMessages, setFlaggedMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [speechEnabled, setSpeechEnabled] = useState(true);
  
  // Refs
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  
  // Permissions
  const canModerate = user.id === group.admin_id || 
                     (group.moderators && group.moderators.includes(user.id)) ||
                     user.role === 'admin';

  // WebSocket connection
  const { socket, connectionStatus } = useWebSocket(group.id, user, (message) => {
    if (message.sender_id !== user.id) {
      setNewMessages(prev => [message.id, ...prev]);
      // Notification sonore (optionnelle)
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(`${message.sender_name} dans ${group.name}`, {
          body: message.message,
          icon: '/favicon.ico'
        });
      }
    }
    setMessages(prev => [...prev, message]);
  });

  // Auto-scroll vers le bas
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Marquer comme lu quand on voit les messages
  useEffect(() => {
    if (messages.length > 0 && onMarkAsRead) {
      const timer = setTimeout(() => {
        onMarkAsRead();
        setNewMessages([]);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [messages.length, onMarkAsRead]);

  // Synthèse vocale
  const speak = (text, speakerName) => {
    if (!speechEnabled || !('speechSynthesis' in window)) return;
    
    const utterance = new SpeechSynthesisUtterance(`${speakerName} dit : ${text}`);
    utterance.lang = 'fr-FR';
    utterance.rate = 0.9;
    utterance.pitch = 1;
    speechSynthesis.speak(utterance);
  };

  // Gestion envoi message améliorée
  const handleSendMessage = async (messageText) => {
    console.log('[CHAT_SEND] Attempting to send message:', {
      groupId: group.id,
      userId: user?.id,
      messageLength: messageText.length,
      timestamp: new Date().toISOString()
    });

    // Validation du message
    if (!messageText.trim()) {
      console.log('[CHAT_SEND] Empty message rejected');
      return;
    }

    // Vérifier les permissions utilisateur
    if (!user || !user.id) {
      console.error('[CHAT_SEND] User not authenticated');
      alert('Vous devez être connecté pour envoyer des messages');
      return;
    }

    // Vérifier si l'utilisateur est membre du groupe
    const isMember = group.members?.some(member => member.id === user.id) || group.admin_id === user.id;
    if (!isMember) {
      console.error('[CHAT_SEND] User not member of group');
      alert('Vous ne pouvez pas envoyer de message dans ce groupe');
      return;
    }

    // Vérifier si le groupe est actif
    if (group.status === 'inactive' || group.status === 'closed') {
      console.error('[CHAT_SEND] Group inactive:', group.status);
      alert('Vous ne pouvez pas envoyer de messages dans ce groupe (inactif)');
      return;
    }

    // Validation de la longueur du message
    if (messageText.length > 500) {
      console.error('[CHAT_SEND] Message too long:', messageText.length);
      alert('Message trop long (maximum 500 caractères)');
      return;
    }

    // Créer un message temporaire pour l'UI optimiste
    const tempMessage = {
      id: `temp-${Date.now()}`,
      group_id: group.id,
      sender_id: user.id,
      sender_name: user.username || user.name || 'Utilisateur',
      message: messageText,
      timestamp: new Date().toISOString(),
      is_flagged: false,
      is_temp: true,
      sending: true // Indicateur d'envoi en cours
    };
    
    console.log('[CHAT_SEND] Adding temporary message for optimistic UI');
    
    // Ajouter le message temporaire immédiatement (optimistic UI)
    setMessages(prev => [...prev, tempMessage]);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      console.log('[CHAT_SEND] Sending message to backend...');
      
      // Appel API pour envoyer le message
      const response = await axios.post(`${API}/chat/messages`, {
        group_id: group.id,
        message: messageText,
        timestamp: new Date().toISOString()
      }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000 // 10 secondes timeout
      });

      console.log('[CHAT_SEND] Message sent successfully:', response.data);

      // Remplacer le message temporaire par le message confirmé du serveur
      const confirmedMessage = {
        id: response.data.id || `msg-${Date.now()}`,
        group_id: group.id,
        sender_id: user.id,
        sender_name: user.username || user.name,
        message: messageText,
        timestamp: response.data.timestamp || new Date().toISOString(),
        is_flagged: false,
        is_temp: false,
        sending: false,
        sent: true // Indicateur de succès
      };

      setMessages(prev => prev.map(msg => 
        msg.id === tempMessage.id ? confirmedMessage : msg
      ));

      // Feedback de succès temporaire
      setTimeout(() => {
        setMessages(prev => prev.map(msg => 
          msg.id === confirmedMessage.id ? { ...msg, sent: false } : msg
        ));
      }, 2000);

      console.log('[CHAT_SEND] Message confirmed and UI updated');

    } catch (error) {
      console.error('[CHAT_SEND] Error sending message:', error);
      
      // Marquer le message temporaire comme échoué
      setMessages(prev => prev.map(msg => 
        msg.id === tempMessage.id 
          ? { ...msg, sending: false, failed: true } 
          : msg
      ));

      // Définir le message d'erreur approprié
      let errorMessage = 'Échec d\'envoi, réessayez';
      
      if (error.response?.status === 401) {
        errorMessage = 'Session expirée, veuillez vous reconnecter';
      } else if (error.response?.status === 403) {
        errorMessage = 'Vous n\'êtes pas autorisé à envoyer des messages dans ce groupe';
      } else if (error.response?.status === 429) {
        errorMessage = 'Trop de messages envoyés, attendez un moment';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Délai d\'attente dépassé, vérifiez votre connexion';
      } else if (!navigator.onLine) {
        errorMessage = 'Pas de connexion internet, message non envoyé';
      }

      alert(`❌ ${errorMessage}`);

      // Log détaillé pour debug
      console.error('[CHAT_SEND] Detailed error:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        groupId: group.id,
        userId: user.id,
        messageText: messageText.substring(0, 50) + '...',
        timestamp: new Date().toISOString()
      });

      // Retirer le message échoué après 5 secondes
      setTimeout(() => {
        setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
      }, 5000);
    }
  };

  // Signaler un message
  const handleFlagMessage = async (messageId, message) => {
    try {
      const token = localStorage.getItem('token');
      const reason = prompt('Motif du signalement (optionnel) :');
      
      if (reason === null) return; // User cancelled
      
      await axios.post(`${API}/chat/messages/${messageId}/flag`, 
        { reason: reason || 'Contenu inapproprié' }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Mise à jour locale
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, is_flagged: true } : msg
      ));
      
      // Notification pour l'admin
      if (canModerate) {
        alert('Message signalé et ajouté à la modération');
      } else {
        alert('Message signalé. L\'administrateur en sera informé.');
      }
      
    } catch (error) {
      console.error('Erreur signalement:', error);
      alert('Erreur lors du signalement');
    }
  };

  // Supprimer un message
  const handleDeleteMessage = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/chat/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
      setFlaggedMessages(prev => prev.filter(msg => msg.id !== messageId));
      
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  // Charger messages signalés
  const loadFlaggedMessages = async () => {
    if (!canModerate) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/chat/groups/${group.id}/flagged`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFlaggedMessages(response.data);
    } catch (error) {
      console.error('Erreur chargement messages signalés:', error);
    }
  };

  // Charger les messages signalés au montage si admin
  useEffect(() => {
    if (canModerate) {
      loadFlaggedMessages();
    }
  }, [canModerate]);

  return (
    <div className="flex flex-col h-full bg-gray-50">
      
      {/* Header du chat */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => {
              console.log('[CHAT] Back button clicked');
              try {
                if (onBack) {
                  console.log('[CHAT] Using onBack callback');
                  onBack();
                } else {
                  console.log('[CHAT] No onBack callback, using history.back()');
                  window.history.back();
                }
              } catch (error) {
                console.error('[CHAT] Error during back navigation:', error);
                // Fallback: try to go to previous page
                try {
                  window.history.back();
                } catch (fallbackError) {
                  console.error('[CHAT] Fallback navigation also failed:', fallbackError);
                  // Last resort: reload page to get out of broken state
                  window.location.reload();
                }
              }
            }}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors mr-2"
            aria-label="Retour"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white text-sm font-bold">💬</span>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Discussion de groupe</h3>
              <p className="text-xs text-gray-500">
                {messages.length} messages • {connectionStatus}
              </p>
            </div>
          </div>
          
          {/* Badge messages non lus */}
          {unreadCount > 0 && (
            <div className="bg-red-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
              {unreadCount > 99 ? '99+' : unreadCount}
            </div>
          )}
        </div>

        {/* Actions header */}
        <div className="flex items-center gap-2">
          
          {/* Toggle synthèse vocale */}
          <button
            onClick={() => setSpeechEnabled(!speechEnabled)}
            className={`p-2 rounded-lg transition-colors ${
              speechEnabled ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'
            }`}
            title={speechEnabled ? 'Désactiver la lecture vocale' : 'Activer la lecture vocale'}
          >
            {speechEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>

          {/* Modération (admin seulement) */}
          {canModerate && (
            <button
              onClick={() => {
                setShowModerationPanel(true);
                loadFlaggedMessages();
              }}
              className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors relative"
              title="Ouvrir la modération"
            >
              <Shield className="w-4 h-4" />
              {flaggedMessages.length > 0 && (
                <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {flaggedMessages.length}
                </div>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Zone des messages */}
      <div 
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-1"
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Chargement des messages...</span>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">💬</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Commencez la conversation !
            </h3>
            <p className="text-gray-600">
              Soyez le premier à envoyer un message à votre groupe.
            </p>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <ChatMessage
                key={message.id}
                message={message}
                currentUser={user}
                canModerate={canModerate}
                onFlag={handleFlagMessage}
                onDelete={handleDeleteMessage}
                onSpeak={speak}
                isNew={newMessages.includes(message.id)}
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Zone de saisie */}
      <ChatInput 
        onSendMessage={handleSendMessage}
        disabled={isLoading}
        placeholder="Écrivez votre message..."
      />

      {/* Panel de modération */}
      <ModerationPanel
        group={group}
        flaggedMessages={flaggedMessages}
        onDeleteMessage={handleDeleteMessage}
        onUnflagMessage={(messageId) => {
          setFlaggedMessages(prev => prev.filter(msg => msg.id !== messageId));
        }}
        isVisible={showModerationPanel}
        onClose={() => setShowModerationPanel(false)}
      />
    </div>
  );
};

export default GroupChat;